import type { ConversationChannelScope } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { SessionContextItem } from '@atlassian/rovo-platform-ui-components/session-details/SessionContext';

const JIRA_ISSUE_KEY_PATTERN = /^[A-Z][A-Z0-9_]*-\d+$/;
const JIRA_BROWSE_PATH_PATTERN = /^\/browse\/([A-Z][A-Z0-9_]*-\d+)\/?$/;

const getHttpUrl = (link: string): string | undefined => {
	try {
		const url = new URL(link.trim());
		if (url.protocol !== 'https:' && url.protocol !== 'http:') {
			return undefined;
		}

		return url.href;
	} catch {
		return undefined;
	}
};

const getScopeLink = (scope: ConversationChannelScope): string => {
	const link = scope.link as unknown;
	if (typeof link === 'string') {
		return link.trim();
	}
	if (link !== null && typeof link === 'object') {
		const value = (link as Record<string, unknown>).url ?? (link as Record<string, unknown>).href;
		return typeof value === 'string' ? value.trim() : '';
	}
	return '';
};

const getContextUrl = (scope: ConversationChannelScope): string | undefined => {
	const link = getScopeLink(scope);
	const httpUrl = getHttpUrl(link);
	if (httpUrl) {
		return httpUrl;
	}

	const isFilePreview = scope.type === 'file' || scope.type === 'image';
	return isFilePreview && link.startsWith('/') && !link.startsWith('//') ? link : undefined;
};

const mapScope = (scope: ConversationChannelScope): SessionContextItem | null => {
	const url = getContextUrl(scope);
	const label = scope.name.trim();
	if (!url) {
		return null;
	}

	const iconUrl = scope.icon_url == null ? undefined : getHttpUrl(scope.icon_url);

	const common = {
		id: url,
		url,
		...(iconUrl == null ? {} : { iconUrl }),
	};

	if (scope.type === 'confluence') {
		if (!label) {
			return null;
		}

		return {
			...common,
			label,
			type: 'confluence',
			...(scope.sub_type == null ? {} : { subtype: scope.sub_type }),
		};
	}

	if (scope.type === 'jira') {
		const issueKey =
			new URL(url).pathname.match(JIRA_BROWSE_PATH_PATTERN)?.[1] ||
			(JIRA_ISSUE_KEY_PATTERN.test(label) ? label : undefined);
		return issueKey ? { ...common, label: issueKey, type: 'jira' } : null;
	}

	if (!label) {
		return null;
	}

	return {
		...common,
		label,
		type: scope.type,
		...(scope.sub_type == null ? {} : { subtype: scope.sub_type }),
	};
};

export const getSessionListItemContexts = (
	scopes: readonly ConversationChannelScope[] | undefined,
): readonly SessionContextItem[] | undefined => {
	const contexts = scopes?.flatMap((scope) => {
		const context = mapScope(scope);
		return context == null ? [] : [context];
	});

	return contexts?.length ? contexts : undefined;
};
